import { useEffect, useState } from "react";
import GeoJSON from "ol/format/GeoJSON";
import { Feature, Map } from "ol";
import { useVectorLayer } from "../utils/useVectorLayer";
import Style from "ol/style/Style";
import Fill from "ol/style/Fill";
import { request } from "@/request";
import getBackendURL from "@/api/api";
import { getReferenceColor } from "../../styles";
import { isValidId } from "@/actions/util";


const format = new GeoJSON({
	dataProjection: "EPSG:4326",
	featureProjection: "EPSG:3857",
});

interface ResolvedShapes {
	features: Feature[];
	resolvedEsrId: string;
}

const NOTHING: ResolvedShapes = { features: [], resolvedEsrId: "" };

async function fetchGeodata(
	esrId: string,
	questionId: string,
	exclusive = false,
): Promise<ResolvedShapes> {
	const url = getBackendURL(
		`/esr/geodata/${esrId}/${questionId}/${exclusive ? "?exclusive=1" : ""}`,
	);
	const { data } = await request(url);
	return {
		features: format.readFeatures(data.geojson) as Feature[],
		resolvedEsrId: String(data.esr_id),
	};
}

async function fetchReferenceShapes(
	viewingId: string,
	questionFrom: string,
	questionId: string,
): Promise<ResolvedShapes> {
	if (!isValidId(viewingId)) return NOTHING;

	// E92's reference is the version it replaced; anything else sits inside the
	// project limits in force here.
	if (questionId === "E92") {
		if (questionFrom !== viewingId) return NOTHING;
		return fetchGeodata(questionFrom, questionId, true);
	}
	return fetchGeodata(viewingId, "E92");
}

export function useReferenceLayer(
	map: Map | null,
	viewingId: string, // ESR whose shapes are on screen (initial or addendum)
	questionFrom: string, // ESR the question layer's shapes came from
	questionId: string,
	shapeVersion: number,
	enabled: boolean,
) {
	const [mapLoading, setMapLoading] = useState(false);
	const [showingFrom, setShowingFrom] = useState("");

	const { source, layer } = useVectorLayer(map, {
		// above the basemap tiles, which default to 0, but below the question layer
		zIndex: 1,
		renderOrder: (f1, f2) => {
			// Draw state_row last so it appears on top of project_limits
			if (f1.get("group") === "state_row") return 1;
			if (f2.get("group") === "state_row") return -1;
			return 0;
		},
		style: new Style({
			fill: new Fill({ color: getReferenceColor(questionId, "E92") }),
		}),
	});

	useEffect(() => {
		if (!source) return;

		let cancelled = false;
		source.clear();
		if (enabled && viewingId) {
			setMapLoading(true);
			fetchReferenceShapes(viewingId, questionFrom, questionId).then((resolved) => {
				setMapLoading(false);
				if (cancelled) return;
				source.addFeatures(resolved.features);
				setShowingFrom(resolved.resolvedEsrId);
			});
		}

		return () => {
			cancelled = true;
			source.clear();
		};
	}, [shapeVersion, viewingId, questionFrom, questionId, enabled]);

	return { source, layer, mapLoading, showingFrom };
}